import type {
  CurrentSubscription,
  InitialResponseV5,
  ScheduledPlay,
  UserSubscriptionAccess,
  UserSubscriptionFeature,
} from '@/types/initial-request';

export type SubscriptionLimits = {
  eventLimit: number;
  smsLimit: number;
  playersPerSchedule: number;
  maxCourtsPerEvent: number;
  maxRoundRobinsPerMonth: number;
  maxPlayersPerRoundRobins: number;
  maxSdEliminationTournamentsPerMonth: number;
  maxPlayersPerSdEliminationTournaments: number;
};

export type SubscriptionUsage = Record<string, number>;

export type SubscriptionLimitKey = 'sms_limit' | 'players_per_schedule' | 'event_limit';

export const DEFAULT_SUBSCRIPTION_LIMITS: SubscriptionLimits = {
  eventLimit: 1,
  smsLimit: 15,
  playersPerSchedule: 5,
  maxCourtsPerEvent: 1,
  maxRoundRobinsPerMonth: 1,
  maxPlayersPerRoundRobins: 8,
  maxSdEliminationTournamentsPerMonth: 0,
  maxPlayersPerSdEliminationTournaments: 0,
};

const SUBSCRIPTION_GRACE_DAYS_MS = 2 * 24 * 60 * 60 * 1000;
const COURT_INTEL_SKU_PREFIX = 'com.whoplays.venue.courtintel.';
const COURT_INTEL_FEATURE_KEY_PREFIXES = ['venue.', 'courtintel.', 'court_intel.', 'court-intel.'];
const COURT_INTEL_LIMIT_KEYS = new Set(['venue.max_cameras', 'venue.max_venues']);

export function buildSubscriptionUsage(data?: InitialResponseV5): SubscriptionUsage {
  const usage: SubscriptionUsage = {};
  (data?.userActivityUsageMonthly ?? []).forEach((entry) => {
    const key = entry.activityType?.trim();
    if (!key) return;
    const value = entry.activityCount ?? 0;
    usage[key] = Math.max(usage[key] ?? 0, value);
  });
  return usage;
}

export function getActivityUsage(usage: SubscriptionUsage, featureKey: string) {
  return usage[featureKey] ?? 0;
}

export function isExceededSMSLimit(usage: SubscriptionUsage, limits: SubscriptionLimits) {
  return getActivityUsage(usage, 'sms_limit') >= limits.smsLimit;
}

export function isExceededPlayersPerSchedule(limits: SubscriptionLimits, playersInvited: number) {
  return playersInvited > limits.playersPerSchedule;
}

export function isExceededEventLimit(
  plays: ScheduledPlay[] | undefined,
  scheduleId: string | undefined,
  limits: SubscriptionLimits,
) {
  if (!plays?.length) return false;
  let eventCount = 0;
  plays.forEach((play) => {
    if (scheduleId && play.scheduleId === scheduleId) return;
    if (play.invitations && play.invitations.length > 0) {
      eventCount += 1;
    }
  });
  return eventCount >= limits.eventLimit;
}

export function hasCourtIntelligenceAccess(data?: InitialResponseV5, nowMs = Date.now()) {
  return (
    hasActiveCourtIntelSubscription(data?.currentSubscription, nowMs) ||
    hasActiveCourtIntelAccessGrant(data?.userSubscriptionAccess, nowMs)
  );
}

function hasActiveCourtIntelSubscription(subscription?: CurrentSubscription | null, nowMs = Date.now()) {
  if (!subscription) return false;
  if (!isWithinGracePeriod(subscription.expiresAt, nowMs)) return false;
  if (!isStatusActive(subscription.status, subscription.expiresAt)) return false;
  const sku = resolveSubscriptionSku(subscription);
  return isCourtIntelSku(sku) || hasCourtIntelFeature(subscription.features);
}

function hasActiveCourtIntelAccessGrant(access?: UserSubscriptionAccess | null, nowMs = Date.now()) {
  if (!access) return false;
  if (!isWithinGracePeriod(access.expiresAt, nowMs)) return false;
  return isCourtIntelSku(access.sku) || hasCourtIntelFeature(access.features);
}

function isWithinGracePeriod(expiresAt?: string, nowMs = Date.now()) {
  if (!expiresAt) return true;
  const expires = Date.parse(expiresAt);
  if (Number.isNaN(expires)) return true;
  return nowMs <= expires + SUBSCRIPTION_GRACE_DAYS_MS;
}

function isStatusActive(status?: string, expiresAt?: string) {
  if (!status) return true;
  const normalized = status.toLowerCase();
  if (expiresAt) {
    return !['inactive', 'expired'].includes(normalized);
  }
  return !['canceled', 'cancelled', 'expired', 'inactive'].includes(normalized);
}

function resolveSubscriptionSku(subscription?: CurrentSubscription | null) {
  if (!subscription) return null;
  return subscription.plan?.sku ?? subscription.sku ?? null;
}

function isCourtIntelSku(sku?: string | null) {
  return Boolean(sku?.toLowerCase().startsWith(COURT_INTEL_SKU_PREFIX));
}

function hasCourtIntelFeature(features?: UserSubscriptionFeature[]) {
  if (!features?.length) return false;
  return features.some((feature) => {
    const key = feature.featureKey?.trim().toLowerCase();
    if (!key) return false;
    const hasCapacity = feature.limitValue === null || feature.limitValue === undefined || feature.limitValue > 0;
    if (!hasCapacity) return false;
    if (COURT_INTEL_LIMIT_KEYS.has(key)) return true;
    return COURT_INTEL_FEATURE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
  });
}
